<script setup lang="ts">
// Idioma
const { locale, t } = useI18n()
const browserLang = navigator?.language ?? locale.value
const { isPackFirstFlow } = useCheckoutFlowExperiment()

// Props
const props = defineProps({
  data: Object,
  arrayHoras: Object,
  urlParams: String,
  alternative: Boolean,
  /** Fecha alternativa de disponibilidad (p. ej. día ±1 en búsqueda ampliada). */
  availableDate: {
    type: Date,
    required: false,
    default: undefined
  },
  /** Fecha principal de la búsqueda (para PostHog en clics de hoteles con badge de fecha). */
  searchDate: {
    type: String,
    required: false,
    default: undefined
  },
  dateRangeMode: {
    type: String,
    required: false,
    default: undefined
  }
})

// Promo
const hotelPromos = computed<Promo[]>(() => {
  const seen = new Set<string>()
  return (props.data?.promo ?? []).filter((p: Promo) => {
    if (!p.short_description || seen.has(p.short_description)) return false
    seen.add(p.short_description)
    return true
  })
})

const uniquePromos = computed(() => hotelPromos.value.map(p => p.short_description))

const firstUniquePromo = computed(() => uniquePromos.value[0] ?? null)

const hasMultiplePromos = computed(() => hotelPromos.value.length > 1)

const multiplePromosBadgeLabel = computed(() =>
  t('searchCard.promos.multiple', { count: hotelPromos.value.length })
)

const openPromosModal = ref(false)

const availableDateBadgeLabel = computed(() => {
  if (!props.availableDate) return null
  return `${t('strapi.searchPage.disponibleEl')} ${formatFakeDate(props.availableDate)}`
})

const showAvailableDateBadge = computed(() => !!availableDateBadgeLabel.value)

// Imagenees
const images = computed(() => props.data?.images)

// Hover event
const hoverCarousel = ref(false)
const hoverCard = ref(false)

const emit = defineEmits(['hoverHotel', 'toggle-favorite'])
watch(hoverCard, (newValue) => {
  if (newValue) {
    emit('hoverHotel', props.data?.id)
  } else {
    emit('hoverHotel', 0)
  }
})

const deviceStore = useDeviceStore();
const isMobile = deviceStore.isMobile;

// PostHog funnel
const { trackHotelCardClicked, trackSearchDateRangeHotelClicked } = usePosthogFunnel()
const currencyStore = useCurrencyStore()
const { currency } = storeToRefs(currencyStore)

function trackHotelCardClick() {
  if (!props?.data?.id) return
  const minPrice = props.data?.pack_min_price?.[3]?.price ?? props.data?.pack_min_price?.[6]?.price ?? props.data?.pack_min_price?.[24]?.price
  trackHotelCardClicked({
    hotel_id: props.data.id,
    hotel_name: props.data?.name,
    date: undefined,
    currency: currency.value,
    min_price: minPrice ?? undefined,
    is_mobile: isMobile
  })
  if (props.availableDate && props.searchDate) {
    trackSearchDateRangeHotelClicked({
      hotel_id: Number(props.data.id),
      hotel_name: props.data?.name,
      search_date: props.searchDate,
      available_date: formatDateGuion(props.availableDate),
      date_range_mode: (props.dateRangeMode as 'plus1' | 'minus1' | 'both' | '') || undefined,
      currency: currency.value,
      min_price: minPrice ?? undefined,
      is_mobile: isMobile
    })
  }
}

const openModalPiscina = ref(false)

const open = ref(false)
const activeIndex = ref(0)
function onSelect(index: number) {
  activeIndex.value = index
}

const PACK_HOURS = [3, 6, 24] as const

const availableHours = computed(() => {
  return PACK_HOURS.filter(hour => props.arrayHoras?.[hour]?.length)
})

const packWindowLabels = computed(() => {
  const times = props.data?.checkin_times ?? {}
  const allDay = t('roomCard.packWindow.allDay')
  return {
    3: getPackWindowLabel(3, times['3'], browserLang, allDay),
    6: getPackWindowLabel(6, times['6'], browserLang, allDay),
    24: getPackWindowLabel(24, times['24'], browserLang, allDay)
  } as Record<number, string>
})

/** Precio mínimo entre packs con disponibilidad (ancla "desde" en pack-first). */
const minPriceLabel = computed(() => {
  const packs = props.data?.pack_min_price ?? {}
  let bestPrice = Infinity
  let bestLabel = ''
  for (const pack of PACK_HOURS) {
    if (!props.arrayHoras?.[pack]?.length) continue
    const slot = packs[pack]
    const price = slot?.price
    if (typeof price !== 'number' || Number.isNaN(price)) continue
    if (price < bestPrice) {
      bestPrice = price
      bestLabel = slot?.price_label ?? ''
    }
  }
  return bestLabel || primerPrecioLabel(props.data)
})

/** Ventana de disfrute unificada (solo packs 3h y 6h). */
const hotelEnjoymentWindowLabel = computed(() => {
  const allDay = t('roomCard.packWindow.allDay')
  return getHotelEnjoymentWindowLabel(
    props.data?.checkin_times ?? {},
    availableHours.value,
    browserLang,
    allDay,
    props.data?.rooms as Record<string, {
      checkin_times?: Record<string, Record<string, boolean | unknown>>
      promo?: { type?: number }
    }> | undefined,
    props.data?.promo as Array<{ type?: number }> | undefined
  )
})

const hotelEnjoymentAriaLabel = computed(() => {
  if (!hotelEnjoymentWindowLabel.value) return ''
  return `${t('roomCard.packOption.availabilityTitle')}: ${hotelEnjoymentWindowLabel.value}`
})

const availablePacksValueLabel = computed(() => {
  const hours = availableHours.value
  if (!hours.length) return ''
  if (hours.length === 1) {
    return t('searchCard.availablePacks.one', { pack: hours[0] })
  }
  const packs = hours.map(pack => `${pack}h`).join(' · ')
  return t('searchCard.availablePacks.many', { packs })
})

function buildHotelUrl(options?: { pack?: number, hora?: string, scrollTo?: string }) {
  const slug = props?.data?.slug || slugify(props?.data?.name || 'hotel')
  const params = new URLSearchParams()
  const baseQuery = props.urlParams ?? ''
  if (baseQuery) {
    for (const [key, value] of new URLSearchParams(baseQuery)) {
      params.set(key, value)
    }
  }
  if (options?.pack) params.set('pack', String(options.pack))
  if (options?.hora) params.set('hora', options.hora)
  if (options?.scrollTo) params.set('scrollTo', options.scrollTo)
  const query = params.toString()
  return `/${locale.value}/${slug}-${props?.data?.id}.html${query ? `?${query}` : ''}`
}

function navigateToHotel(options?: { pack?: number, hora?: string }) {
  trackHotelCardClick()
  const url = buildHotelUrl({
    pack: options?.pack,
    hora: isPackFirstFlow.value ? undefined : options?.hora,
    scrollTo: isPackFirstFlow.value ? 'seccion_habitaciones' : undefined
  })
  navigateTo(url, { open: { target: props.alternative ? '_self' : '_blank' } })
}

function navigateToRoomsSection() {
  trackHotelCardClick()
  const url = buildHotelUrl({ scrollTo: 'seccion_habitaciones' })
  navigateTo(url, { open: { target: props.alternative ? '_self' : '_blank' } })
}

function packModalDescription(pack: number): string {
  const windowLabel = packWindowLabels.value[pack]
  if (windowLabel) {
    return t('checkout.checkinHour.description', { hours: pack, window: windowLabel })
  }
  return t('checkout.checkinHour.descriptionNoWindow', { hours: pack })
}

/** Mínimo de habitaciones disponibles en cualquier pack del hotel (el cuello de botella). */
const minRoomsAvailable = computed(() => {
  const rooms = props.data?.rooms ?? {}
  let total = 0
  let found = false
  for (const roomId of Object.keys(rooms)) {
    const packs = (rooms as Record<string, { packs?: Record<string, { num_rooms_available?: number }> }>)[roomId]?.packs ?? {}
    let roomMin = Infinity
    for (const packKey of ['3', '6', '24']) {
      const n = packs[packKey]?.num_rooms_available
      if (typeof n === 'number' && n < roomMin) roomMin = n
    }
    if (roomMin !== Infinity) {
      total += roomMin
      found = true
    }
  }
  return found ? total : 0
})

/** Umbral: mostrar badge de urgencia si quedan menos de esta cantidad. */
const URGENCY_THRESHOLD = 2
const showUrgencyBadge = computed(() => {
  const n = minRoomsAvailable.value
  return n > 0 && n < URGENCY_THRESHOLD
})

const showPromosRow = computed(
  () => uniquePromos.value.length > 0 || showUrgencyBadge.value || showAvailableDateBadge.value
)

const showPackFirstPromoSlot = computed(
  () => uniquePromos.value.length > 0 || showUrgencyBadge.value || showAvailableDateBadge.value
)

/** Parsea una hora "HH:mm" o "hh:mm AM/PM" a minutos desde medianoche (0-1439). */
function parseTimeToMinutes(timeStr: string): number {
  if (!timeStr || typeof timeStr !== 'string') return 0
  const hasAmPm = /AM|PM/i.test(timeStr)
  if (hasAmPm) {
    const [h, m] = timeStr.replace(/\s*(AM|PM)/i, '').trim().split(':').map(Number)
    const isPm = /PM/i.test(timeStr)
    const hours = (h ?? 0) % 12 + (isPm ? 12 : 0)
    return (hours * 60) + (m ?? 0)
  }
  const [h, m] = timeStr.split(':').map(Number)
  return ((h ?? 0) * 60) + (m ?? 0)
}

/** Formatea minutos al mismo estilo que los botones actuales: 24h → "10h", 12h → "10AM". */
function formatMinutesToButtonLabel(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24
  if (!changeHourFormat(browserLang)) {
    const pad = (n: number) => n.toString().padStart(2, '0')
    return `${pad(h)}h`
  }
  const hour12 = h % 12 || 12
  const suffix = h < 12 ? 'AM' : 'PM'
  return `${hour12}${suffix}` // sin cero a la izquierda, igual que los botones
}

/** Rango global min-max de horas de entrada (solo variante control). */
const globalHoursRangeLabel = computed(() => {
  const arr = props.arrayHoras
  if (!arr) return ''
  let minM = Infinity
  let maxM = 0
  for (const key of PACK_HOURS) {
    const times: string[] = arr[key] ?? []
    for (const time of times) {
      const m = parseTimeToMinutes(time)
      if (m < minM) minM = m
      if (m > maxM) maxM = m
    }
  }
  if (minM === Infinity || maxM === 0) return ''
  const minStr = formatMinutesToButtonLabel(minM)
  const maxStr = formatMinutesToButtonLabel(maxM)
  if (minStr === maxStr) return minStr
  return `${minStr} - ${maxStr}`
})

const availabilityButtonLabel = computed(() => globalHoursRangeLabel.value)

// Favorites Store
const favoritesStore = useFavoritesStore()

// Estado local del favorito
const favorite = computed(() => {
  return favoritesStore.isFavorite(props?.data?.id)
})

</script>

<template>
  <div class="relative h-full gap-4 sm:gap-3 flex flex-col sm:flex-row rounded-2xl" @mouseover="hoverCard = true"
    @mouseleave="hoverCard = false">

    <div :class="[
      'relative sm:w-5/12 lg:w-4/12 2xl:w-5/12 rounded-2xl bg-neutral-100 shrink-0',
      hoverCard
        ? 'shadow-[0px_0px_20px_0_rgba(0,0,0,0.1)]'
        : 'shadow-[0px_0px_10px_0_rgba(0,0,0,0.1)]'
    ]" :style="{
      transition: 'box-shadow 0.3s cubic-bezier(0.4,0,0.2,1)'
    }">
      <!-- Carousel -->
      <UCarousel :arrows="!isMobile && hoverCarousel ? true : false" @mouseover="hoverCarousel = true"
        @mouseleave="hoverCarousel = false" v-slot="{ item }" :items="images" :class="`h-full sm:min-h-44`"
        :next="{ size: 'xs', variant: 'soft' }" :prev="{ size: 'xs', variant: 'soft' }" :ui="{
          item: 'basis-full h-full',
          container: 'h-full',
          viewport: 'aspect-16/9 w-full h-full rounded-2xl',
          next: 'sm:end-2',
          prev: 'sm:start-2'
        }" @select="onSelect">
        <NuxtImg provider="byhours" format="webp" width="600" height="400" :src="generateImgUrl(String(item ?? ''))"
          @click="trackHotelCardClick(); navigateTo(`/${locale}/${props?.data?.slug || slugify(props?.data?.name || 'hotel')}-${props?.data?.id}.html?${urlParams ?? ''}`, { open: { target: alternative ? '_self' : '_blank' } })"
          :alt="`Imagen hotel`" class="w-full h-full object-cover cursor-pointer rounded-2xl" draggable="false"
          loading="lazy" />
      </UCarousel>

      <!-- Indicador de posición -->
      <div v-if="isMobile && images?.length > 1" class="absolute bottom-2 right-2 z-10">
        <div class="bg-neutral-900/70 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">
          {{ activeIndex + 1 }}/{{ images?.length }}
        </div>
      </div>

      <!-- Promo/urgencia (test) o horas disponibles (control) -->
      <div class="absolute top-0 left-0 z-10 max-w-[calc(100%-3rem)] p-2">
        <div
          v-if="isPackFirstFlow && showPackFirstPromoSlot"
          class="flex max-w-full flex-wrap items-center gap-1.5"
        >
          <UBadge
            v-if="showAvailableDateBadge"
            color="neutral"
            icon="i-lucide-calendar"
            variant="soft"
            size="md"
            :label="availableDateBadgeLabel!"
          />
          <UBadge
            v-else-if="hasMultiplePromos"
            :aria-label="multiplePromosBadgeLabel"
            color="secondary"
            variant="soft"
            size="md"
            icon="i-lucide-tag"
            :label="multiplePromosBadgeLabel"
            class="bg-cyan-50 cursor-pointer"
            @click.stop.prevent="openPromosModal = true"
          />
          <UBadge
            v-else-if="firstUniquePromo"
            :aria-label="$t('strapi.searchPage.filtroPromo')"
            color="secondary"
            variant="soft"
            size="md"
            :label="firstUniquePromo"
            class="bg-cyan-50"
          />
          <UBadge
            v-else-if="showUrgencyBadge"
            color="primary"
            variant="soft"
            size="md"
            class="bg-orange-50"
            :label="minRoomsAvailable === 1 ? t('searchCard.urgency.oneRoom') : t('searchCard.urgency.moreRooms', { count: minRoomsAvailable })"
          />
        </div>

        <UModal
          v-else-if="!isPackFirstFlow"
          v-model:open="open"
          :ui="{ content: 'max-w-2xl max-h-[80vh] overflow-y-auto' }"
          :title="$t('labels.horasDisponibles')"
        >
          <UButton
            :label="$t('buttons.dispo')"
            color="neutral"
            variant="soft"
            size="xs"
            icon="i-lucide-clock"
            class="shrink-0"
            :ui="{ base: 'drop-shadow-md' }"
            @click.stop.prevent="open = true"
          />
          <template #body>
            <div class="text-xs">
              <!-- Pack-first: franjas por pack, sin elegir hora -->
              <template v-if="isPackFirstFlow">
                <div v-for="key in availableHours" :key="key" class="pb-6 last:pb-0">
                  <div class="font-bold mb-1">{{ key }} {{ $t('labels.horas') }}</div>
                  <p v-if="packWindowLabels[key]" class="mb-2 text-neutral-600">
                    <span class="text-neutral-400">{{ $t('roomCard.packOption.availabilityTitle') }}:</span>
                    {{ packWindowLabels[key] }}
                  </p>
                  <p class="mb-3 text-[11px] leading-snug text-neutral-500">
                    {{ packModalDescription(key) }}
                  </p>
                  <UButton
                    color="primary"
                    variant="solid"
                    size="sm"
                    :label="data?.pack_min_price?.[key]?.price_label
                      ? `${t('searchCard.viewRooms')} · ${data.pack_min_price[key].price_label}`
                      : t('searchCard.viewRooms')"
                    @click="navigateToHotel({ pack: key })"
                  />
                </div>
              </template>
              <!-- Control: hora a hora -->
              <template v-else>
                <div v-for="key in availableHours" :key="key">
                  <div v-if="arrayHoras?.[key]?.length" class="pb-6">
                    <div class="font-bold mb-2">{{ key }}h</div>
                    <div class="flex flex-wrap gap-2 sm:gap-3">
                      <div v-for="item in arrayHoras?.[key]" :key="`${key}-${item}`">
                        <UButton
                          :label="arrayHoras?.[key]?.length === 1 ? item : `${item} - ${addHoursToTimeLabel(item, key)}`"
                          color="neutral"
                          variant="outline"
                          size="xs"
                          @click="navigateToHotel({ pack: key, hora: item })"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </template>
        </UModal>
      </div>

      <!-- Botón de favorito -->
      <div class="absolute top-0 right-0 p-1.5 z-10">
        <UButton :aria-label="$t('labels.aria.favorito')"
          :icon="favorite ? 'i-heroicons-heart-solid' : 'i-heroicons-heart'" color="neutral" size="sm" variant="soft"
          class="hover:scale-105 transition-all duration-300" :ui="{
            base: 'drop-shadow-md',
            leadingIcon: favorite ? 'text-error' : 'text-neutral-900'
          }" @click.stop.prevent="emit('toggle-favorite', props?.data?.id)" />
      </div>

    </div>

    <transition name="fade-card" mode="out-in" :duration="300">
      <NuxtLink
        :to="`/${locale}/${props?.data?.slug || slugify(props?.data?.name || 'hotel')}-${props?.data?.id}.html?${urlParams ?? ''}`"
        :target="alternative ? '_self' : '_blank'" @click="trackHotelCardClick" :class="[
          'bg-transparent px-0 sm:bg-white sm:p-5',
          'transition-all duration-300 w-full sm:w-7/12 lg:w-8/12 flex flex-col sm:justify-between rounded-2xl min-w-0',
          'shadow-none',
          !isMobile ? 'hover:shadow-[0px_0px_20px_0_rgba(0,0,0,0.1)]' : 'hover:shadow-none',
          'sm:shadow-[0px_0px_10px_0_rgba(0,0,0,0.1)]'
        ]" :aria-label="props?.data?.name">
        <div class="flex flex-col gap-0.5 min-w-0 sm:flex-1">

          <!-- Info hotel -->
          <div class="flex justify-between items-center gap-2">

            <div class="flex gap-2 items-center -mt-1 min-w-0 flex-1">
              <div v-if="splitEstrellas(props?.data?.stars?.abbr).countEstrellas" class="shrink-0">
                <UIcon :key="index" v-for="index in splitEstrellas(props?.data?.stars?.abbr).countEstrellas"
                  name="i-heroicons-star-solid" class="text-xs" />
                <span class="text-sm">{{ splitEstrellas(props?.data?.stars?.abbr).rightString }}</span>
              </div>
              <p class="text-xs text-neutral-500 truncate">{{ data?.city }}</p>
            </div>

            <!-- Promos and Tags -->
            <div class="flex items-center gap-1 shrink-0 -mt-2 sm:-mt-4 sm:-mr-2.5">
              <template v-for="item in data?.tag" :key="item.id">

                <!-- Tag Top hotel -->
                <div v-if="item.id === 7" class="relative">
                  <UBadge :label="item.name" color="warning" variant="soft" size="sm" class="shine-effect" />
                  <div class="star">
                    <UIcon name="i-lucide-sparkle" class="text-2xs" />
                  </div>
                </div>

                <!-- Tag piscina -->
                <div v-else-if="item.id === 25">
                  <div v-if="isMobile">
                    <UModal v-model:open="openModalPiscina">
                      <UBadge @touchend.prevent.stop="openModalPiscina = true" :label="item.name" color="info"
                        variant="soft" size="sm" class="shine-effect hover:cursor-pointer" icon="ic:baseline-pool" />
                      <template #header>
                        <p class="font-bold">{{ item?.name }}</p>
                      </template>
                      <template #body>
                        <p class="py-2">
                          {{ $t('labels.tags.piscina') }}
                        </p>
                      </template>
                    </UModal>
                  </div>
                  <div v-else>
                    <UTooltip :delay-duration="100" :text="$t('labels.tags.piscina')" :content="{ side: 'top' }">
                      <UBadge :label="item.name" color="info" variant="soft" size="sm"
                        class="shine-effect hover:cursor-pointer" icon="ic:baseline-pool" />
                    </UTooltip>
                  </div>
                </div>

                <!-- Tag Spa  -->
                <div v-else-if="item.id === 27">
                  <UBadge :label="item.name" color="info" variant="soft" size="sm"
                    class="shine-effect hover:cursor-pointer" icon="ic:baseline-spa" />
                </div>

                <!-- Tag Amor amistad  -->
                <div v-else-if="item.id === 28">
                  <UBadge :label="item.name" variant="soft" size="sm" class="shine-effect hover:cursor-pointer"
                    icon="i-heroicons-heart-solid" :ui="{
                      leadingIcon: 'text-pink-500',
                      base: 'text-pink-500 bg-pink-500/10'
                    }" />
                </div>

                <!-- Tag normal -->
                <UBadge v-else :label="item.name" color="neutral" size="sm" variant="outline" />
              </template>
            </div>
          </div>

          <p class="text-base sm:text-lg font-bold mb-1.5 line-clamp-2">{{ data?.name }}</p>

          <!-- Urgencia (solo control; en test va junto a propuesta de valor) -->
          <UBadge
            v-if="!isPackFirstFlow && showUrgencyBadge && !showPromosRow"
            color="neutral"
            variant="solid"
            size="md"
            :label="minRoomsAvailable === 1 ? t('searchCard.urgency.oneRoom') : t('searchCard.urgency.moreRooms', { count: minRoomsAvailable })"
            class="self-start"
          />

          <div v-if="!isPackFirstFlow && showPromosRow"
            class="flex items-center w-full mb-2 min-w-0 gap-2 flex-wrap">
            <UBadge v-if="showAvailableDateBadge" color="neutral" icon="i-lucide-calendar" variant="soft" size="md"
              :label="availableDateBadgeLabel!" class="shrink-0" />
            <UBadge
              v-if="hasMultiplePromos"
              :aria-label="multiplePromosBadgeLabel"
              color="secondary"
              variant="soft"
              size="md"
              icon="i-lucide-tag"
              :label="multiplePromosBadgeLabel"
              class="shrink-0 cursor-pointer"
              @click.prevent.stop="openPromosModal = true"
            />
            <UBadge
              v-else-if="firstUniquePromo"
              :aria-label="$t('strapi.searchPage.filtroPromo')"
              color="secondary"
              variant="soft"
              size="md"
              :label="firstUniquePromo"
              class="shrink-0"
            />
          </div>

          <div v-if="!isPackFirstFlow" class="flex w-full gap-4 items-center mb-1 min-w-0">
            <div v-if="availabilityButtonLabel" class="shrink-0">
              <UButton
                color="neutral"
                size="sm"
                :ui="{ base: 'px-5 py-2.5 shrink-0 max-w-full' }"
                variant="outline"
                leading-icon="i-lucide-door-open"
                :label="availabilityButtonLabel"
                @click.prevent.stop="navigateToRoomsSection"
              />
            </div>

            <div class="flex flex-wrap gap-x-4 pt-0.5 flex-col shrink-0">
              <div class="flex items-center gap-1">
                <UIcon name="i-lucide-badge-check" class="w-3.5 h-3.5 text-success-500" aria-hidden="true" />
                <span class="text-[10px] text-success-500">{{ $t('roomCard.badges.freeCancellation') }}</span>
              </div>
              <div class="flex items-center gap-1">
                <UIcon :name="data?.tpv ? 'i-lucide-credit-card' : 'i-lucide-building-2'"
                  class="w-3.5 h-3.5 text-success-500" aria-hidden="true" />
                <span class="text-[10px] text-success-500">{{ data?.tpv ? $t('roomCard.badges.payNow') :
                  $t('roomCard.badges.payAtDestination') }}</span>
              </div>
            </div>
          </div>

        </div>

        <!-- Precio y packs -->
        <div class="mt-1 shrink-0 min-w-0">
          <div v-if="isPackFirstFlow" class="flex min-w-0 flex-col gap-3">
            <UButton
              v-if="hotelEnjoymentWindowLabel"
              color="neutral"
              variant="outline"
              size="sm"
              :label="hotelEnjoymentWindowLabel"
              :aria-label="hotelEnjoymentAriaLabel"
              class="self-start max-w-full shrink-0"
              @click.prevent.stop="navigateToRoomsSection"
            />

            <div class="mt-1 flex min-w-0 items-end justify-between gap-3 sm:gap-4">
              <div class="flex min-w-0 flex-1 flex-col gap-2">
                <UBadge icon="i-lucide-bed-double" class="w-fit" v-if="availablePacksValueLabel" color="success" variant="soft" :label="availablePacksValueLabel" />
                <div class="flex min-w-0 items-center gap-1.5 pl-2">
                  <UIcon name="i-lucide-badge-check" class="size-4 shrink-0 text-success-500" aria-hidden="true" />
                  <span class="text-xs leading-tight text-success-500 whitespace-nowrap">{{ $t('roomCard.badges.freeCancellation') }}</span>
                </div>
                <div class="flex min-w-0 items-center gap-1.5 pl-2">
                  <UIcon :name="data?.tpv ? 'i-lucide-credit-card' : 'i-lucide-building-2'"
                    class="size-4 shrink-0 text-success-500" aria-hidden="true" />
                  <span class="text-xs leading-tight text-success-500 whitespace-nowrap">{{ data?.tpv ? $t('roomCard.badges.payNow') :
                    $t('roomCard.badges.payAtDestination') }}</span>
                </div>
              </div>

              <div v-if="minPriceLabel" class="flex shrink-0 flex-col items-end pl-1">
                <span class="text-md text-neutral-500">{{ $t('buttons.desde').toLowerCase() }}</span>
                <span class="text-2xl font-semibold leading-tight text-neutral-900 whitespace-nowrap">{{ minPriceLabel }}</span>
              </div>
            </div>
          
          </div>
          <div v-else class="grid grid-rows-1 grid-flow-col grid-cols-3 gap-3">
            <div
              v-for="pack in PACK_HOURS"
              :key="pack"
              v-show="data?.pack_min_price?.[pack]?.price_label && arrayHoras?.[pack]?.length"
            >
              <div class="text-sm text-neutral-500">{{ pack }} {{ $t('labels.horas') }}</div>
              <div class="text-lg/6 sm:text-lg font-semibold">{{ data?.pack_min_price?.[pack]?.price_label }}</div>
            </div>
          </div>
        </div>
      </NuxtLink>
    </transition>

    <UModal
      v-if="!isMobile"
      v-model:open="openPromosModal"
      :title="$t('searchCard.promos.modalTitle')"
      :ui="{ content: 'max-w-lg max-h-[80vh] overflow-y-auto' }"
    >
      <template #body>
        <ul class="flex flex-col gap-4">
          <li v-for="(promo, index) in hotelPromos" :key="index" class="flex flex-col gap-1">
            <p class="text-sm font-semibold">{{ promo.short_description }}</p>
            <p v-if="promo.description" class="text-sm text-neutral-600">{{ promo.description }}</p>
          </li>
        </ul>
      </template>
    </UModal>

    <UDrawer
      v-if="isMobile"
      v-model:open="openPromosModal"
      direction="bottom"
      :title="$t('searchCard.promos.modalTitle')"
    >
      <template #body>
        <ul class="flex flex-col gap-4 pb-6">
          <li v-for="(promo, index) in hotelPromos" :key="index" class="flex flex-col gap-1">
            <p class="text-sm font-semibold">{{ promo.short_description }}</p>
            <p v-if="promo.description" class="text-sm text-neutral-600">{{ promo.description }}</p>
          </li>
        </ul>
      </template>
    </UDrawer>

  </div>
</template>

<style scoped>
.shine-effect {
  position: relative;
  overflow: hidden;
}

.shine-effect::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 50%;
  height: 100%;
  background: linear-gradient(to right,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent);
  transform: skewX(-25deg);
  animation: shine 15s infinite;
  z-index: 2;
}

.star {
  position: absolute;
  top: -4px;
  right: -2px;
  font-size: 11px;
  color: #fbbf24;
  opacity: 1;
  z-index: 1;
}

@keyframes shine {
  0% {
    left: -100%;
  }

  8% {
    left: 100%;
  }

  18% {
    left: -100%;
  }

  26% {
    left: 100%;
  }

  36% {
    left: -100%;
  }

  44% {
    left: 100%;
  }

  54% {
    left: -100%;
  }

  62% {
    left: 100%;
  }

  100% {
    left: 100%;
  }
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

@keyframes heartbeat {

  0%,
  100% {
    transform: scale(1);
  }

  25% {
    transform: scale(1.2);
  }

  50% {
    transform: scale(0.95);
  }

  75% {
    transform: scale(1.1);
  }
}

.heartbeat-animation {
  animation: heartbeat 2s ease-in-out;
}
</style>